import React, { createContext, useState, useContext, useEffect } from 'react';
import { api, setApiAuthToken } from '../api/api';

const AuthContext = createContext(null);
const TOKEN_SESSION_KEY = 'token';
const TOKEN_PERSIST_KEY = 'token_persistent';
const ACTIVE_GROUP_KEY = 'active_group_id';

const getAssignedGroupIds = userObj => {
  const ids = new Set();
  const primary = Number(userObj?.group?.id || userObj?.groupId || userObj?.group_id);
  if (Number.isFinite(primary) && primary > 0) ids.add(primary);
  if (Array.isArray(userObj?.groups)) {
    userObj.groups.forEach(group => {
      const parsed = Number(group?.id || group?.groupId || group?.group_id);
      if (Number.isFinite(parsed) && parsed > 0) ids.add(parsed);
    });
  }
  return Array.from(ids);
};

const resolveActiveGroupId = (userObj, storedValue) => {
  const assignedIds = getAssignedGroupIds(userObj);
  if (!assignedIds.length) return null;
  const candidate = Number(storedValue);
  if (Number.isFinite(candidate) && candidate > 0 && assignedIds.includes(candidate)) {
    return candidate;
  }
  return assignedIds[0];
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeGroupId, setActiveGroupIdState] = useState(null);

  useEffect(() => {
    const storedUser = sessionStorage.getItem('user');
    const persistentToken = localStorage.getItem(TOKEN_PERSIST_KEY) || '';
    const sessionToken = sessionStorage.getItem(TOKEN_SESSION_KEY) || '';
    const legacyToken =
      sessionStorage.getItem('token') ||
      localStorage.getItem('token') ||
      '';
    const token = sessionToken || persistentToken || legacyToken;
    if (token) {
      setApiAuthToken(token);
      if (!sessionToken) {
        sessionStorage.setItem(TOKEN_SESSION_KEY, token);
      }
    }
    if (storedUser && token) {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
      const resolved = resolveActiveGroupId(
        parsedUser,
        sessionStorage.getItem(ACTIVE_GROUP_KEY)
      );
      if (resolved) {
        sessionStorage.setItem(ACTIVE_GROUP_KEY, String(resolved));
        setActiveGroupIdState(resolved);
      } else {
        sessionStorage.removeItem(ACTIVE_GROUP_KEY);
        setActiveGroupIdState(null);
      }
    } else if (storedUser && !token) {
      // Prevent stale authenticated UI state without a usable auth token.
      sessionStorage.removeItem('user');
    }
    setLoading(false);
  }, []);

  const login = async (username, password, rememberMe = false) => {
    let response;
    try {
      response = await api.login(username, password, rememberMe);
    } catch (error) {
      return { success: false, message: error?.message || 'Login request failed.' };
    }
    if (response.success) {
      if (response.token) {
        setApiAuthToken(response.token);
        sessionStorage.setItem(TOKEN_SESSION_KEY, response.token);
        if (rememberMe) {
          localStorage.setItem(TOKEN_PERSIST_KEY, response.token);
        } else {
          localStorage.removeItem(TOKEN_PERSIST_KEY);
        }
      }
      let patchedUser = { ...response.user };
      if (patchedUser.mustChangePassword) {
        sessionStorage.setItem('user', JSON.stringify(patchedUser));
        setUser(patchedUser);
        return { success: true, user: patchedUser, mustChangePassword: true };
      }
      sessionStorage.setItem('user', JSON.stringify(patchedUser));
      setUser(patchedUser);
      const resolved = resolveActiveGroupId(
        patchedUser,
        sessionStorage.getItem(ACTIVE_GROUP_KEY)
      );
      if (resolved) {
        sessionStorage.setItem(ACTIVE_GROUP_KEY, String(resolved));
        setActiveGroupIdState(resolved);
      } else {
        sessionStorage.removeItem(ACTIVE_GROUP_KEY);
        setActiveGroupIdState(null);
      }
      // Ensure fresh start on login: remove any previous template/config selection (all keys)
      try {
        const keys = Object.keys(sessionStorage);
        keys.forEach(k => {
          if (k.startsWith('currentConfig')) sessionStorage.removeItem(k);
        });
      } catch {}
      return { success: true, user: patchedUser };
    }
    return { success: false, message: response.message };
  };

  const logout = () => {
    sessionStorage.removeItem('user');
    sessionStorage.removeItem(TOKEN_SESSION_KEY);
    sessionStorage.removeItem(ACTIVE_GROUP_KEY);
    localStorage.removeItem(TOKEN_PERSIST_KEY);
    setUser(null);
    setActiveGroupIdState(null);
    setApiAuthToken('');
    api.logout().catch(() => {});
    try {
      const keys = Object.keys(sessionStorage);
      keys.forEach(k => {
        if (k.startsWith('currentConfig')) sessionStorage.removeItem(k);
      });
    } catch {}
  };

  const forcePasswordUpdate = async (userId, newPassword) => {
    const response = await api.changePassword(userId, newPassword);
    if (response.success) {
      const updatedUser = response.user
        ? { ...response.user }
        : { ...user, mustChangePassword: false };
      sessionStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      return { success: true };
    }
    return { success: false, message: 'Failed to update password.' };
  };

  const markOnboardingComplete = async () => {
    if (!user) return { success: false, message: 'No authenticated user' };
    const result = await api.markOnboardingComplete(user.id);
    if (result.success && result.user) {
      const updatedUser = { ...result.user };
      sessionStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
    }
    return result;
  };

  const resetOnboarding = async () => {
    if (!user) return { success: false, message: 'No authenticated user' };
    const result = await api.resetOnboarding(user.id);
    if (result.success && result.user) {
      const updatedUser = { ...result.user };
      sessionStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
    }
    return result;
  };

  const value = {
    user,
    activeGroupId,
    setActiveGroupId: nextId => {
      const resolved = resolveActiveGroupId(user, nextId);
      if (resolved) {
        sessionStorage.setItem(ACTIVE_GROUP_KEY, String(resolved));
        setActiveGroupIdState(resolved);
      } else {
        sessionStorage.removeItem(ACTIVE_GROUP_KEY);
        setActiveGroupIdState(null);
      }
    },
    login,
    logout,
    forcePasswordUpdate,
    loading,
    isAuthenticated: !!user,
    markOnboardingComplete,
    resetOnboarding,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
